import { Chip, Stack, Typography, Theme } from '@mui/material';
import { SystemStyleObject } from '@mui/system';
import { memo, useCallback } from 'react';

import SkeletonBox from 'components/generic/SkeletonBox';
import { choiceTextString } from 'components/Inputs/Dropdown/SelectDropdown/selectUtils';
import { RenderValueElementProps } from 'components/Inputs/Dropdown/SelectDropdown/SelectRenderValueElem';
import { bloxValues } from 'themes/constants';
import { getChipsRenderTypoSx } from 'components/Inputs/Dropdown/dropdownConsts';
import { useTranslation } from 'react-i18next';
import { useBatchEdit } from '../context';

const getChipsRenderChipSx = (theme: Theme, isError: boolean): SystemStyleObject => ({
  fontSize: bloxValues.tinyFontSizeRem,
  outlineOffset: '-1px',
  whiteSpace: 'normal',
  height: 'auto',
  '& .MuiChip-label': {
    fontWeight: isError
      ? theme.typography.fontWeightMedium
      : theme.typography.fontWeightRegular,
    display: 'block',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    px: 1,
    py: 0.5,
  },
});

export const BatchEditRenderValueElem = memo(
  ({
    selectedChoices,
    placeholderText,
    isLoading,
    isError,
    isDataPrivate,
    group = '',
  }: RenderValueElementProps) => {
    const { addRemoveValues } = useBatchEdit();
    const { t } = useTranslation();
    const removeLabel = t('blox.actions.remove');

    const getChipColor = useCallback(
      (choice: any) => {
        if (!group) return 'default';

        const groupItems = addRemoveValues[group];
        const data = groupItems?.find((item: any) => item.value === choice.value);

        // Special case for "Remove" chips that are API add flow
        const isRemoveChip =
          (choice?.label === removeLabel && choice?.value === '') ||
          choice?.value === 'remove';

        if (data) {
          if (isRemoveChip) return 'error';

          switch (data?.misc?.action) {
            case 'add':
              return 'success';
            case 'remove':
              return 'error';
            default:
              return 'default';
          }
        }

        return isRemoveChip ? 'error' : 'default';
      },
      [addRemoveValues, group, removeLabel],
    );

    if (isLoading) {
      return <SkeletonBox height={24} />;
    }

    const buildChipsJSX = selectedChoices.map((choice: any, idx: any) => {
      return (
        <Chip
          key={`chips-${choice.value}-${idx}`}
          size="small"
          variant="filled"
          label={`${choiceTextString(choice)}`}
          sx={(theme) => getChipsRenderChipSx(theme, Boolean(isError))}
          data-private={isDataPrivate ? true : undefined}
          color={getChipColor(choice)}
        />
      );
    });

    return (
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 0.5,
          width: '100%',
          maxHeight: 57,
          overflow: 'auto',
        }}
      >
        {selectedChoices.length > 0 ? (
          buildChipsJSX
        ) : (
          <Typography
            data-private={isDataPrivate ? true : undefined}
            sx={getChipsRenderTypoSx}
          >
            {placeholderText}
          </Typography>
        )}
      </Stack>
    );
  },
);
BatchEditRenderValueElem.displayName = 'BatchEditRenderValueElem';
